import React, { useState } from 'react';
import useDidUpdateEffect from '@use-effect/did-update';
import slugify from 'slugify';
import { useTheme } from 'styled-components';

import { InformationLetter } from '@solent-university/solent-icons';

import Section from 'Elements/Section';
import Checkbox from 'Elements/Input/Checkbox';
import { ArrowLinkProps } from 'Elements/Link/Arrow';
import ArrowButton, { ArrowLinkButtonProps } from 'Elements/Button/Arrow';
import Button from 'Elements/Button';
import Select from 'Elements/Input/Dropdown';

import Grid, { Col } from 'Components/Grid';
import ListBlock from 'Components/Block/List';
import Modal from 'Components/Modal';
import Mini, { MiniCourseCardProps } from 'Components/Card/Course/Mini';

import {
    Top,
    Heading,
    Listing,
    ListItem,
    ButtonWrapper,
    CoursesViewedText,
    ViewAllLinksContainer,
    ViewAllLinksHeading,
    LinksWrapper,
    StyledArrowLink,
    Filters,
    SelectWrapper,
    StyledIcon,
    FilterPillButton,
    InfoWrapper,
    PillWrapper,
    ResultsText,
    ResultsNumber,
    FilterInfoButton,
    FilterInfoIcon,
    NoResults
} from './Course.styled';

export interface ActiveFilter {
    id: string;
    type: string;
    label: string;
}

export interface Filter {
    id: string;
    name: string;
    label: string;
    value: string;
}

export interface FilterGroup {
    type: string;
    label: string;
    options: Filter[];
}

export interface Props {
    id: string;
    heading: string;
    courses: MiniCourseCardProps[];
    paddingTop?: boolean;
    promo?: {
        promoText: string;
        arrowLink?: ArrowLinkProps;
        arrowButton?: ArrowLinkButtonProps;
    };
    filterProps: {
        id?: string;
        filters: FilterGroup[];
        filterInfoModal?: {
            modalChildren: React.ReactChild;
            linkText: string;
        };
    };
}

const Course: React.FC<Props> = props => {
    const { id, heading, courses, paddingTop = false, promo, filterProps } = props;

    const { colors } = useTheme();

    const initialCoursesListLength = 6;
    const noOfCoursesToAddToList = 6;

    const [coursesList, setCoursesList] = useState(courses);
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
    const [filterInfoActiveModal, setFilterInfoActiveModal] = useState(false);
    const [coursesToDisplay, setCoursesToDisplay] = useState(initialCoursesListLength);

    const addToCoursesList = () => {
        setCoursesToDisplay(coursesToDisplay + noOfCoursesToAddToList);
    };

    useDidUpdateEffect(() => {
        filterResults();
    }, [activeFilters]);

    const addFilter = (id: string, type: string, label: string) => {
        setActiveFilters([...activeFilters, { id, type, label }]);
    };

    const removeFilter = (id: string) => {
        const findAndRemoveFilter = (filters: ActiveFilter[]): ActiveFilter[] => {
            const updatedFilters: ActiveFilter[] = [];

            for (const filter of filters) {
                if (filter.id === id) {
                    continue;
                } else {
                    updatedFilters.push(filter);
                }
            }

            return updatedFilters;
        };

        setActiveFilters(findAndRemoveFilter(activeFilters));
    };

    const handleFilter = (id: string, checked: boolean, type: string, label: string) => {
        if (checked) {
            addFilter(id, type, label);
        } else {
            removeFilter(id);
        }
    };

    const filterResults = () => {
        let filteredCourses = courses;

        // filters to show all courses with a filtered study level
        const studyLevelFilters = activeFilters.filter(filter => filter.type === 'studyLevel');
        const studyLevelFiltersLabels =
            studyLevelFilters.length > 0 && studyLevelFilters.map(filter => filter.label);
        studyLevelFiltersLabels &&
            (filteredCourses = filteredCourses.filter(
                course => course.studyLevel && studyLevelFiltersLabels.includes(course.studyLevel)
            ));

        // filters to show all courses with a filtered course option
        const courseOptionFilters = activeFilters.filter(filter => filter.type === 'courseOptions');
        const courseOptionFilterLabels =
            courseOptionFilters.length > 0 && courseOptionFilters.map(filter => filter.label);
        courseOptionFilterLabels &&
            (filteredCourses = filteredCourses.filter(
                course =>
                    course.courseOptions &&
                    course.courseOptions.length > 0 &&
                    course.courseOptions.some(courseOption =>
                        courseOptionFilterLabels.includes(courseOption)
                    )
            ));

        setCoursesList(filteredCourses);
    };

    const generateCheckboxes = (filter: FilterGroup) =>
        filter.options.map(item => {
            const activeFilter = activeFilters.find(({ id }) => id === item.id);
            return (
                <React.Fragment key={`filter-${item.id}`}>
                    <Checkbox
                        defaultChecked={!!activeFilter}
                        handleCheckedChange={checked =>
                            handleFilter(item.id, checked, filter.type, item.label)
                        }
                        {...item}
                    />
                </React.Fragment>
            );
        });

    const filtersJSX = (
        <>
            {filterProps.filters.map(filter => (
                <SelectWrapper key={filter.type} $type={filter.type}>
                    <Select
                        button={{
                            ariaLabel: filter.label,
                            label: filter.label
                        }}
                        id={filter.type}
                    >
                        <ListBlock
                            fieldset={true}
                            id={`fieldset-${slugify(filter.type, {
                                lower: true
                            })}`}
                            maxHeight={filter.options.length > 8}
                        >
                            {generateCheckboxes(filter)}
                        </ListBlock>
                    </Select>
                </SelectWrapper>
            ))}
        </>
    );

    const pillsJSX = (
        <>
            {activeFilters.map(item => (
                <React.Fragment key={`filter-${item.id}`}>
                    <FilterPillButton
                        onClick={() => removeFilter(item.id)}
                        aria-label={`Remove ${item.id} course filter`}
                    >
                        {`${item.label}`}
                        <StyledIcon />
                    </FilterPillButton>
                </React.Fragment>
            ))}
        </>
    );

    return (
        <Section id={id} background paddingTop={paddingTop ? 1 : undefined}>
            <Grid>
                <Col>
                    <Top>
                        <Heading>{heading}</Heading>
                        <Section id="filters" paddingTop={0.5} paddingBottom="none" background>
                            <form onSubmit={e => e.preventDefault()} id={filterProps.id}>
                                <Grid>
                                    <Col>
                                        <Filters>{filtersJSX}</Filters>
                                        {filterProps.filterInfoModal && (
                                            <FilterInfoButton
                                                type="button"
                                                onClick={() => setFilterInfoActiveModal(true)}
                                                aria-label="Learn more about these filter categories"
                                                aria-expanded={filterInfoActiveModal}
                                            >
                                                <FilterInfoIcon
                                                    as={InformationLetter}
                                                    id="filter-info-icon"
                                                />
                                                {filterProps.filterInfoModal.linkText}
                                            </FilterInfoButton>
                                        )}
                                    </Col>
                                </Grid>
                                <Grid>
                                    <Col>
                                        <InfoWrapper
                                            $filterInfoModal={
                                                filterProps.filterInfoModal ? true : false
                                            }
                                        >
                                            <PillWrapper>{pillsJSX}</PillWrapper>
                                            <ResultsText>
                                                Showing
                                                <ResultsNumber>
                                                    {' '}
                                                    {coursesList.length} courses{' '}
                                                </ResultsNumber>
                                                that fit your criteria
                                            </ResultsText>
                                        </InfoWrapper>
                                    </Col>
                                </Grid>
                            </form>
                        </Section>
                    </Top>
                    {coursesList.length > 0 ? (
                        <Listing>
                            {coursesList.slice(0, coursesToDisplay).map((course, index) => (
                                <ListItem key={index}>
                                    <Mini {...course} />
                                </ListItem>
                            ))}
                        </Listing>
                    ) : (
                        <NoResults>The filters you’ve selected do not match any courses.</NoResults>
                    )}
                    {coursesList.length > coursesToDisplay && (
                        <ButtonWrapper>
                            <CoursesViewedText>{`You've viewed ${coursesToDisplay} of ${coursesList.length} courses`}</CoursesViewedText>
                            <Button
                                onClick={() => addToCoursesList()}
                                label="Load more"
                                ariaLabel="Load more courses"
                                type="button"
                                variant="accentFiveOutline"
                            />
                        </ButtonWrapper>
                    )}
                    {promo && (promo.arrowButton || promo.arrowLink) && (
                        <ViewAllLinksContainer>
                            <ViewAllLinksHeading>{promo.promoText}</ViewAllLinksHeading>
                            <LinksWrapper>
                                {promo.arrowLink && <StyledArrowLink {...promo.arrowLink} />}
                                {promo.arrowButton && <ArrowButton {...promo.arrowButton} />}
                            </LinksWrapper>
                        </ViewAllLinksContainer>
                    )}
                </Col>
            </Grid>
            {filterProps.filterInfoModal && (
                <Modal
                    id="study_level_modal"
                    heading="The difference in study levels"
                    headingType="h2"
                    headingColour={colors.accents[7].hex}
                    isVisible={filterInfoActiveModal}
                    closeFnc={() => setFilterInfoActiveModal(false)}
                >
                    {filterProps.filterInfoModal.modalChildren}
                </Modal>
            )}
        </Section>
    );
};

export default Course;
